import { getSearchablePlanningTagText } from './tags';

const CORE_SEARCH_FIELDS = [
  'reference_no',
  'reference',
  'ref',
  'application_ref',
  'address',
  'location',
  'title',
  'description',
  'ai_summary',
  'ai_description',
  'ai_desc',
  'summary',
  'category',
  'application_type',
  'status',
  'decision',
  'decision_status',
  'application_status',
  'decision_outcome',
  'interest_reason',
  'scale_rating',
] as const;

const RICH_SEARCH_FIELDS = [
  'tags',
  'tag_details',
  'interest_signals',
  'documents',
  'documentUrls',
  'application_form',
  'application_form_data',
  'application_form_json',
  'application_form_parsed',
  'form_data',
  'parsed_form_data',
  'planning_application_form',
  'application_insights',
  'insights',
] as const;

const MAX_RECURSIVE_ITEMS = 120;

const humanizeKey = (value: string): string => value.replace(/[_-]+/g, ' ');

const pushText = (parts: string[], value: unknown) => {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      parts.push(trimmed, humanizeKey(trimmed));
    }
    return;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    parts.push(String(value));
  }
};

const flattenSearchValue = (value: unknown, parts: string[], depth = 0, counter = { count: 0 }) => {
  if (counter.count >= MAX_RECURSIVE_ITEMS || depth > 4) return;

  if (value === null || value === undefined) return;

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    counter.count += 1;
    pushText(parts, value);
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      if (counter.count >= MAX_RECURSIVE_ITEMS) break;
      flattenSearchValue(item, parts, depth + 1, counter);
    }
    return;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    Object.entries(record).forEach(([key, nested]) => {
      if (counter.count >= MAX_RECURSIVE_ITEMS) return;
      if (typeof nested === 'string' || typeof nested === 'number' || typeof nested === 'boolean') {
        parts.push(humanizeKey(key));
      }
      flattenSearchValue(nested, parts, depth + 1, counter);
    });
  }
};

export const getSearchablePlanningApplicationText = (
  application: Record<string, unknown> | null | undefined
): string => {
  if (!application) return '';

  const parts: string[] = [];

  CORE_SEARCH_FIELDS.forEach((field) => {
    pushText(parts, application[field]);
  });

  const tagText = getSearchablePlanningTagText(application);
  pushText(parts, tagText);

  RICH_SEARCH_FIELDS.forEach((field) => {
    flattenSearchValue(application[field], parts);
  });

  return Array.from(new Set(parts.filter(Boolean))).join(' ');
};
